import { z } from "zod";
import {
  LABEL_LIMITS,
  LABEL_SOURCES,
  MAX_LABEL_COPIES,
  MAX_LABEL_RECORDS,
  MAX_LABELS_PER_PRINT,
  type LabelLayout,
  type LabelPreset,
  type LabelSource,
} from "@cx/domain";
import { ulidParam } from "./ids";

/**
 * Etiquetas customizadas (T3.6, RF-CAD-06). Medidas em décimos de mm (inteiros). O formato vem daqui; caber na
 * etiqueta/folha e o campo existir na fonte são conferidos por `validateLabelLayout` (domínio).
 */
const L = LABEL_LIMITS;
const pos = z.number().int().min(0).max(L.maxSize);
const size = z.number().int().min(1).max(L.maxSize);
const fontSize = z.number().int().min(L.minFont).max(L.maxFont);
const align = z.enum(["left", "center", "right"]);
const field = z.string().regex(/^[a-z][a-z0-9_]{0,39}$/, "Campo inválido");
const box = { x: pos, y: pos, w: size, h: size };

export const labelElementInput = z.discriminatedUnion("type", [
  z.strictObject({
    type: z.literal("text"),
    ...box,
    text: z.string().trim().min(1).max(L.maxText),
    fontSize,
    bold: z.boolean().default(false),
    align: align.default("left"),
  }),
  z.strictObject({
    type: z.literal("field"),
    ...box,
    field,
    prefix: z.string().max(L.maxPrefix).default(""),
    fontSize,
    bold: z.boolean().default(false),
    align: align.default("left"),
    maxLines: z.number().int().min(1).max(L.maxLines).default(1),
  }),
  z.strictObject({
    type: z.literal("barcode"),
    ...box,
    field,
    symbology: z.enum(["ean13", "code128"]),
    showText: z.boolean().default(true),
  }),
  z.strictObject({
    type: z.literal("qrcode"),
    x: pos,
    y: pos,
    field,
    size: z.number().int().min(50).max(L.maxSize),
  }),
  z.strictObject({
    type: z.literal("line"),
    x: pos,
    y: pos,
    w: z.number().int().min(0).max(L.maxSize),
    h: z.number().int().min(0).max(L.maxSize),
    thickness: z.number().int().min(1).max(20),
  }),
]);

const margin = z.number().int().min(0).max(500);
export const labelLayoutInput = z.strictObject({
  width: z.number().int().min(L.minSize).max(L.maxSize),
  height: z.number().int().min(L.minSize).max(L.maxSize),
  page: z.discriminatedUnion("kind", [
    z.strictObject({
      kind: z.literal("sheet"),
      paper: z.enum(["A4", "LETTER"]),
      columns: z.number().int().min(1).max(L.maxColumns),
      rows: z.number().int().min(1).max(L.maxRows),
      marginTop: margin,
      marginLeft: margin,
      gapX: margin,
      gapY: margin,
    }),
    z.strictObject({ kind: z.literal("roll") }),
  ]),
  zplDpi: z.union([z.literal(203), z.literal(300)]).default(203),
  elements: z.array(labelElementInput).min(1, "Inclua pelo menos um elemento").max(L.maxElements),
}) satisfies z.ZodType<LabelLayout, unknown>;

export const labelTemplateInput = z.strictObject({
  name: z.string().trim().min(1, "Informe o nome").max(80),
  source: z.enum(LABEL_SOURCES),
  layout: labelLayoutInput,
});
export type LabelTemplateInput = z.infer<typeof labelTemplateInput>;

export const LABEL_PRINT_FORMATS = ["pdf", "zpl"] as const;
export type LabelPrintFormat = (typeof LABEL_PRINT_FORMATS)[number];

export const labelPrintInput = z
  .strictObject({
    items: z
      .array(
        z.strictObject({ id: ulidParam, copies: z.number().int().min(1).max(MAX_LABEL_COPIES).default(1) }),
      )
      .min(1, "Escolha pelo menos um registro")
      .max(MAX_LABEL_RECORDS),
    format: z.enum(LABEL_PRINT_FORMATS).default("pdf"),
    /** Etiquetas já usadas no começo da primeira folha. */
    skip: z.number().int().min(0).max(399).default(0),
  })
  .refine((v) => v.items.reduce((a, i) => a + i.copies, 0) <= MAX_LABELS_PER_PRINT, {
    message: `Até ${MAX_LABELS_PER_PRINT} etiquetas por impressão`,
    path: ["items"],
  })
  .refine((v) => new Set(v.items.map((i) => i.id)).size === v.items.length, {
    message: "Registro repetido: use as cópias",
    path: ["items"],
  });
export type LabelPrintInput = z.infer<typeof labelPrintInput>;

export const labelPreviewInput = z.strictObject({
  source: z.enum(LABEL_SOURCES),
  layout: labelLayoutInput,
});
export type LabelPreviewInput = z.infer<typeof labelPreviewInput>;

export type LabelTemplateView = {
  id: string;
  name: string;
  source: LabelSource;
  layout: LabelLayout;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
};
export type LabelTemplatesView = { data: LabelTemplateView[]; presets: LabelPreset[] };
